// ExcelProcessor.ts - Real implementation with xlsx library
import * as XLSX from 'xlsx';

export interface ProcessedExcelData {
  sheets: string[];
  data: { [sheetName: string]: any[] };
  headers: { [sheetName: string]: string[] };
  summary: {
    totalRows: number;
    totalSheets: number;
    fileSize: string;
    processedAt: string;
  };
}

export interface ExcelProcessingOptions {
  maxRows?: number;
  skipEmptyRows?: boolean;
  trimValues?: boolean;
  dateFormat?: string;
}

/**
 * Excel Processing Class with real xlsx library integration
 * Install: npm install xlsx
 */
export class ExcelProcessor {

  /**
   * Read and process Excel file with real xlsx library
   */
  static async processFile(
    file: File, 
    options: ExcelProcessingOptions = {}
  ): Promise<ProcessedExcelData> {

    // Use options.maxRows, options.skipEmptyRows, options.trimValues, options.dateFormat directly below

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          if (!data) throw new Error('Failed to read file');

          // Read Excel file using xlsx library
          const workbook = XLSX.read(data, { 
            type: 'array',
            cellText: false,
            cellDates: true
          });

          const processedData = this.extractDataFromWorkbook(workbook, options);
          processedData.summary.fileSize = this.formatFileSize(file.size);

          resolve(processedData);

        } catch (error) {
          reject(new Error(`Processing failed: ${error}`));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      // Read as array buffer for xlsx library
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Extract data from xlsx workbook
   */
  private static extractDataFromWorkbook(
    workbook: XLSX.WorkBook, 
    options: ExcelProcessingOptions
  ): ProcessedExcelData {
    const sheets: string[] = [];
    const data: { [sheetName: string]: any[] } = {};
    const headers: { [sheetName: string]: string[] } = {};
    let totalRows = 0;

    workbook.SheetNames.forEach(sheetName => {
      const worksheet = workbook.Sheets[sheetName];

      // Convert sheet to JSON with proper options
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
        header: 1,
        defval: '',
        raw: false,
        dateNF: 'yyyy-mm-dd'
      });

      if (jsonData.length === 0) return;

      // Extract headers (first row)
      const sheetHeaders = (jsonData[0] as any[]).map(header => 
        String(header).trim()
      ).filter(header => header !== '');

      if (sheetHeaders.length === 0) return;

      // Process data rows
      const sheetData = jsonData.slice(1).map(row => {
        const rowObj: any = {};
        const rowArray = row as any[];

        sheetHeaders.forEach((header, index) => {
          let value = rowArray[index];

          // Handle different data types
          if (value === undefined || value === null) {
            value = '';
          } else if (typeof value === 'string' && options.trimValues) {
            value = value.trim();
          } else if (value instanceof Date) {
            value = value.toISOString().split('T')[0]; // Format as YYYY-MM-DD
          } else if (typeof value === 'number') {
            // Keep numbers as is, but check for dates
            if (value > 25000 && value < 50000) { // Excel date range
              try {
                const excelDate = XLSX.SSF.parse_date_code(value);
                if (excelDate) {
                  value = `${excelDate.y}-${String(excelDate.m).padStart(2, '0')}-${String(excelDate.d).padStart(2, '0')}`;
                }
              } catch (e) {
                // Keep as number if date parsing fails
              }
            }
          }

          rowObj[header] = value;
        });

        return rowObj;
      });

      // Filter empty rows if option is enabled
      let filteredData = sheetData;
      if (options.skipEmptyRows) {
        filteredData = sheetData.filter(row => 
          Object.values(row).some(value => 
            value !== '' && value !== null && value !== undefined
          )
        );
      }

      // Limit rows if maxRows is set
      if (options.maxRows && filteredData.length > options.maxRows) {
        filteredData = filteredData.slice(0, options.maxRows);
      }

      sheets.push(sheetName);
      headers[sheetName] = sheetHeaders;
      data[sheetName] = filteredData;
      totalRows += filteredData.length;
    });

    return {
      sheets,
      data,
      headers,
      summary: {
        totalRows,
        totalSheets: sheets.length,
        fileSize: '', // Will be set by caller
        processedAt: new Date().toISOString()
      }
    };
  }

  /**
   * Process CSV file
   */
  static async processCSV(content: string): Promise<ProcessedExcelData> {
    try {
      // Handle different line endings
      const normalizedContent = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      const lines = normalizedContent.split('\n').filter(line => line.trim());

      if (lines.length === 0) throw new Error('Empty CSV file');

      // Parse CSV properly handling quoted values
      const parseCSVLine = (line: string): string[] => {
        const result: string[] = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
          const char = line[i];

          if (char === '"') {
            if (inQuotes && line[i + 1] === '"') {
              current += '"';
              i++; // Skip next quote
            } else {
              inQuotes = !inQuotes;
            }
          } else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }

        result.push(current.trim());
        return result;
      };

      const headers = parseCSVLine(lines[0]).map(h => h.replace(/^"|"$/g, ''));
      const data = lines.slice(1).map(line => {
        const values = parseCSVLine(line);
        const row: any = {};
        headers.forEach((header, index) => {
          let value = values[index] || '';
          value = value.replace(/^"|"$/g, ''); // Remove surrounding quotes
          row[header] = value;
        });
        return row;
      });

      return {
        sheets: ['Sheet1'],
        data: { 'Sheet1': data },
        headers: { 'Sheet1': headers },
        summary: {
          totalRows: data.length,
          totalSheets: 1,
          fileSize: this.formatFileSize(content.length),
          processedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      throw new Error(`CSV processing failed: ${error}`);
    }
  }

  /**
   * Validate Excel file
   */
  static validateFile(file: File): { valid: boolean; error?: string } {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
      'application/csv'
    ];

    if (file.size > maxSize) {
      return { valid: false, error: 'File size must be less than 10MB' };
    }

    const isValidType = validTypes.includes(file.type) || 
                       file.name.match(/\.(xlsx|xls|csv)$/i);

    if (!isValidType) {
      return { 
        valid: false, 
        error: 'File must be Excel (.xlsx, .xls) or CSV format' 
      };
    }

    return { valid: true };
  }

  /**
   * Export processed data to Excel using xlsx library
   */
  static async exportToExcel(
    data: ProcessedExcelData, 
    filename: string = 'processed_data'
  ): Promise<Blob> {
    try {
      // Create a new workbook
      const workbook = XLSX.utils.book_new();

      // Add each sheet to the workbook
      data.sheets.forEach(sheetName => {
        const sheetData = data.data[sheetName] || [];
        const headers = data.headers[sheetName] || [];

        if (sheetData.length === 0) return;

        // Convert data to worksheet format
        const worksheetData = [
          headers,
          ...sheetData.map(row => headers.map(header => row[header] || ''))
        ];

        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

        // Auto-size columns
        const colWidths = headers.map(header => {
          const maxLength = Math.max(
            header.length,
            ...sheetData.map(row => String(row[header] || '').length)
          );
          return { wch: Math.min(maxLength + 2, 50) };
        });
        worksheet['!cols'] = colWidths;

        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      });

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { 
        bookType: 'xlsx', 
        type: 'array',
        compression: true
      });

      return new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });

    } catch (error) {
      console.error('Export error:', error);
      // Fallback to CSV export
      return this.exportToCSV(data, filename);
    }
  }

  /**
   * Export to CSV format
   */
  static async exportToCSV(
    data: ProcessedExcelData, 
    filename: string = 'processed_data'
  ): Promise<Blob> {
    // Use the first sheet for CSV export
    const firstSheet = data.sheets[0];
    const sheetData = data.data[firstSheet] || [];
    const headers = data.headers[firstSheet] || [];

    if (sheetData.length === 0) {
      return new Blob(['No data to export'], { type: 'text/csv' });
    }

    const csvContent = this.convertToCSV(sheetData, headers);
    return new Blob([csvContent], { type: 'text/csv' });
  }

  /**
   * Convert data to CSV format with proper escaping
   */
  private static convertToCSV(data: any[], headers: string[]): string {
    const escapeCSVValue = (value: any): string => {
      if (value === null || value === undefined) return '';

      const stringValue = String(value);

      // Escape values containing commas, quotes, or newlines
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }

      return stringValue;
    };

    const csvRows = [
      headers.map(escapeCSVValue).join(','),
      ...data.map(row => 
        headers.map(header => escapeCSVValue(row[header])).join(',')
      )
    ];

    return csvRows.join('\n');
  }

  /**
   * Format file size
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Generate comprehensive report summary
   */
  static generateSummary(data: ProcessedExcelData): {
    totalRecords: number;
    sheetsAnalysis: { [sheet: string]: any };
    dataQuality: {
      emptyFields: number;
      duplicates: number;
      completeness: string;
    };
    columnAnalysis: { [sheet: string]: { [column: string]: any } };
  } {
    const totalRecords = Object.values(data.data).reduce((sum, sheet) => sum + sheet.length, 0);

    const sheetsAnalysis: { [sheet: string]: any } = {};
    const columnAnalysis: { [sheet: string]: { [column: string]: any } } = {};
    let totalEmptyFields = 0;
    let totalFields = 0;

    Object.keys(data.data).forEach(sheetName => {
      const sheetData = data.data[sheetName];
      const headers = data.headers[sheetName];

      let emptyFields = 0;
      const columnStats: { [column: string]: any } = {};

      // Initialize column stats
      headers.forEach(header => {
        columnStats[header] = {
          emptyCount: 0,
          uniqueValues: new Set(),
          dataTypes: new Set(),
          samples: []
        };
      });

      // Analyze each row
      sheetData.forEach(row => {
        headers.forEach(header => {
          const value = row[header];
          const isEmpty = !value || value === '';

          if (isEmpty) {
            emptyFields++;
            columnStats[header].emptyCount++;
          } else {
            columnStats[header].uniqueValues.add(value);
            columnStats[header].dataTypes.add(typeof value);

            if (columnStats[header].samples.length < 5) {
              columnStats[header].samples.push(value);
            }
          }
        });
      });

      totalEmptyFields += emptyFields;
      totalFields += sheetData.length * headers.length;

      // Finalize column stats
      Object.keys(columnStats).forEach(column => {
        columnStats[column] = {
          emptyCount: columnStats[column].emptyCount,
          uniqueCount: columnStats[column].uniqueValues.size,
          dataTypes: Array.from(columnStats[column].dataTypes),
          samples: columnStats[column].samples,
          completeness: Math.round(((sheetData.length - columnStats[column].emptyCount) / sheetData.length) * 100)
        };
      });

      sheetsAnalysis[sheetName] = {
        rowCount: sheetData.length,
        columnCount: headers.length,
        emptyFields,
        completeness: sheetData.length > 0 ? 
          Math.round(((sheetData.length * headers.length - emptyFields) / (sheetData.length * headers.length)) * 100) + '%' : 
          '0%'
      };

      columnAnalysis[sheetName] = columnStats;
    });

    const completeness = totalFields > 0 ? 
      Math.round(((totalFields - totalEmptyFields) / totalFields) * 100) + '%' : 
      '0%';

    return {
      totalRecords,
      sheetsAnalysis,
      columnAnalysis,
      dataQuality: {
        emptyFields: totalEmptyFields,
        duplicates: 0, // Could implement duplicate detection
        completeness
      }
    };
  }

  /**
   * Search and filter data
   */
  static searchData(
    data: ProcessedExcelData, 
    searchTerm: string, 
    sheetName?: string
  ): ProcessedExcelData {
    const sheetsToSearch = sheetName ? [sheetName] : data.sheets;
    const filteredData = { ...data };

    sheetsToSearch.forEach(sheet => {
      const sheetData = data.data[sheet] || [];
      const filteredRows = sheetData.filter(row =>
        Object.values(row).some(value =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );

      filteredData.data[sheet] = filteredRows;
    });

    return filteredData;
  }

  /**
   * Sort data by column
   */
  static sortData(
    data: ProcessedExcelData, 
    sheetName: string, 
    columnName: string, 
    ascending: boolean = true
  ): ProcessedExcelData {
    const sortedData = { ...data };
    const sheetData = [...(data.data[sheetName] || [])];

    sheetData.sort((a, b) => {
      const aVal = a[columnName];
      const bVal = b[columnName];

      // Handle different data types
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return ascending ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();

      if (ascending) {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });

    sortedData.data[sheetName] = sheetData;
    return sortedData;
  }
}

export default ExcelProcessor;